import {Component, ElementRef, Input, OnInit, ViewChild, Renderer2, Output, EventEmitter} from '@angular/core';
import * as d3 from 'd3';
import {Globals} from '../globals';

@Component({
  selector: 'app-d3bar',
  templateUrl: './d3bar.component.html',
  styleUrls: ['./d3bar.component.css']
})
export class D3barComponent implements OnInit {
  @Input() params: string = '';
  @Input() token: string = '';
  @Input() data:any = null;
  @Output() dataEvent = new EventEmitter<any>();
  @ViewChild('view') panel: ElementRef<HTMLDivElement> | undefined;

  loading: boolean = false;
  title: string = '';
  subtitle: string = '';
  sql:string = "";  // SQL to generate chart data

  constructor(private renderer: Renderer2, private globals: Globals) { }

  ngOnInit(): void {
    if(this.data == null) this.showD3(this.params);
    else setTimeout(()=>this.drawChart(this, this.data), 100); // need time to init the panel
  }

  showD3(params: string): void {

    let me = this;

    me.loading = true;
    let data = d3.json(this.globals.url_root + "/Query.action?sql=SQL_JSON_AIRPORT_CHECKPOINT"+params, {
      headers: new Headers({
        "Authorization": "Bearer " + this.token
      })
    });
    data.then(function(data) {
      me.loading = false;
      me.data = data;
      me.dataEvent.emit(data);
      me.drawChart(me, data);
    });

  }

  drawChart(me: any, data: any): void {

    d3.select('#d3bar').selectAll("*").remove();
    function originalTitle() {
      me.title = "Traffic by Airport";
      me.subtitle = "Click a bar to drill down";
    }

    function chart(svg: any, down: any, d: any, selector: any) {
      const g = svg.insert("g", selector)
        .attr("class", "enter")
        .attr("transform", `translate(0,${margin.top + barStep * barPadding})`)
        .attr("text-anchor", "end")
        .style("font", "10px sans-serif");

      const bar = g.selectAll("g")
        .data(d.children)
        .join("g")
        .attr("cursor", (d: any) => !d.children ? null : "pointer")
        .on("click", (e: any, d: any) => down(svg, d));

      bar.append("text")
        .attr("x", margin.left - 6)
        .attr("y", barStep * (1 - barPadding) / 2)
        .attr("dy", ".35em")
        .text((d: any) => d.data.name);

      bar.append("rect")
        .attr("x", x(0))
        .attr("width", (d:any) => x(d.value) - x(0))
        .attr("height", barStep * (1 - barPadding));

      return g;
    }

    function down(svg:any, d:any) {
      if (!d.children || d3.active(svg.node())) return;

      // Rebind the current node to the background.
      svg.select(".background").datum(d);

      // Define two sequenced transitions.
      const transition1 = svg.transition().duration(duration);
      const transition2 = transition1.transition();

      // Mark any currently-displayed bars as exiting.
      const exit = svg.selectAll(".enter")
        .attr("class", "exit");

      // Entering nodes immediately obscure the clicked-on bar, so hide it.
      exit.selectAll("rect")
        .attr("fill-opacity", (p: any) => p === d ? 0 : null);

      // Transition exiting bars to fade out.
      exit.transition(transition1)
        .attr("fill-opacity", 0)
        .remove();

      // Enter the new bars for the clicked-on data.
      // Per above, entering bars are immediately visible.
      const enter = chart(svg, down, d, ".y-axis")
        .attr("fill-opacity", 0);

      // Have the text fade-in, even though the bars are visible.
      enter.transition(transition1)
        .attr("fill-opacity", 1);

      // Transition entering bars to their new y-position.
      enter.selectAll("g")
        .attr("transform", stack(d.index))
        .transition(transition1)
        .attr("transform", stagger());

      // Update the x-scale domain.
      let max = d3.max(d.children, (d:any)=> (d.value*1));
      if(max != undefined) {
        x.domain([0, max]);
      }

      // Update the x-axis.
      svg.selectAll(".x-axis").transition(transition2)
        .call(xAxis);

      // Transition entering bars to the new x-scale.
      enter.selectAll("g").transition(transition2)
        .attr("transform", (d: any, i: number) => `translate(0,${barStep * i})`);

      // Color the bars as parents; they will fade to children if appropriate.
      enter.selectAll("rect")
        .attr("fill", color(true))
        .attr("fill-opacity", 1)
        .transition(transition2)
        .attr("fill", (d: any) => color(!!d.children))
        .attr("width", (d: any) => x(d.value) - x(0));

      me.title = "Traffic Break-down at " + d.data.name;
      me.subtitle = "Click the background to go back";
    }

    function up(svg: any, d: any) {
      if (!d.parent || !svg.selectAll(".exit").empty()) return;

      // Rebind the current node to the background.
      svg.select(".background").datum(d.parent);

      // Define two sequenced transitions.
      const transition1 = svg.transition().duration(duration);
      const transition2 = transition1.transition();

      // Mark any currently-displayed bars as exiting.
      const exit = svg.selectAll(".enter")
        .attr("class", "exit");

      // Update the x-scale domain.
      let max = d3.max(d.parent.children, (d:any)=> (d.value*1));
      if(max != undefined) {
        x.domain([0, max]);
      }

      // Update the x-axis.
      svg.selectAll(".x-axis").transition(transition1)
        .call(xAxis);

      // Transition exiting bars to the new x-scale.
      exit.selectAll("g").transition(transition1)
        .attr("transform", stagger());

      // Transition exiting bars to the parent’s position.
      exit.selectAll("g").transition(transition2)
        .attr("transform", stack(d.index));

      // Transition exiting rects to the new scale and fade to parent color.
      exit.selectAll("rect").transition(transition1)
        .attr("width", (d: any) => x(d.value) - x(0))
        .attr("fill", color(true));

      // Transition exiting text to fade out.
      // Remove exiting nodes.
      exit.transition(transition2)
        .attr("fill-opacity", 0)
        .remove();

      // Enter the new bars for the clicked-on data's parent.
      const enter = chart(svg, down, d.parent, ".exit")
        .attr("fill-opacity", 0);

      enter.selectAll("g")
        .attr("transform", (d: any, i: number) => `translate(0,${barStep * i})`);

      // Transition entering bars to fade in over the full duration.
      enter.transition(transition2)
        .attr("fill-opacity", 1);

      // Color the bars as appropriate.
      // Exiting nodes will obscure the parent bar, so hide it.
      // Transition entering rects to the new x-scale.
      // When the entering parent rect is done, make it visible!
      enter.selectAll("rect")
        .attr("fill", (d: any) => color(!!d.children))
        .attr("fill-opacity", (p: any) => p === d ? 0 : null)
        .transition(transition2)
        .attr("width", (d: any) => x(d.value) - x(0))
        .on("end", function(d: any, i:number, n:any) { d3.select(n[i]).attr("fill-opacity", 1); });

      originalTitle();
    }

    function stack(i: number) {
      let v = 0;
      return (d: any) => {
        const t = `translate(${x(v) - x(0)},${barStep * i})`;
        v += d.value;
        return t;
      };
    }

    function stagger() {
      let v = 0;
      return (d: any, i: number) => {
        const t = `translate(${x(v) - x(0)},${barStep * i})`;
        v += d.value;
        return t;
      };
    }

    let xAxis = (g: any) => g
      .attr("class", "x-axis")
      .attr("transform", `translate(0,${margin.top})`)
      .call(d3.axisTop(x).ticks(width / 80, "s"))
      .call((g: any) => (g.selection ? g.selection() : g).select(".domain").remove());

    let yAxis = (g: any) => g
      .attr("class", "y-axis")
      .attr("transform", `translate(${margin.left + 0.5},0)`)
      .call((g: any) => g.append("line")
        .attr("stroke", "currentColor")
        .attr("y1", margin.top)
        .attr("y2", height - margin.bottom));

    let margin = ({top: 30, right: 10, bottom: 0, left: 120});
    let color = d3.scaleOrdinal([true, false], ["steelblue", "#aaa"]);
    let barStep = 27;
    let barPadding = 3 / barStep;
    let duration = 750;

    let width = me.panel.nativeElement.offsetWidth;
    let height = me.panel.nativeElement.offsetHeight;

    let root = d3.hierarchy(data)
      .sum((d: any) => d.value)
      .sort((a: any, b: any) => b.value - a.value)
      .eachAfter((d: any) => d.index = d.parent ? d.parent.index = d.parent.index + 1 || 0 : 0);

    let max = 1;
    root.each(d => d.children && (max = Math.max(max, d.children.length)));
    height = max * barStep + margin.top + margin.bottom;

    let svg = d3.select('#d3bar').append("svg")
      .attr("width", width)
      .attr("height", height);

    if(this.panel != null) {
      this.renderer.setStyle(this.panel.nativeElement, 'height', `${height}px`);
    }

    let x = d3.scaleLinear().range([margin.left, width - margin.right]);
    x.domain([0, <number>root.value]);


    svg.append("rect")
      .attr("class", "background")
      .attr("fill", "none")
      .attr("pointer-events", "all")
      .attr("width", width)
      .attr("height", height)
      .attr("cursor", "pointer")
      .on("click", (e:any, d:any) => up(svg, d));

    svg.append("g")
      .call(xAxis);

    svg.append("g")
      .call(yAxis);

    down(svg, root);
    originalTitle();

  }

  onResize(): void {
    if(this.panel != null) {
      this.drawChart(this, this.data);
    }
  }
}
